import {
  AddFolderParams,
  convertFolder,
  RepositoryResponse,
} from '@nextforce/api'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useServer } from 'app/server/context'
import { addMessagesDataToCache } from 'chat/messages/queries/shared/utils'
import { useChatWebsocketContext } from 'chat/websocket/context'
import keys from './shared/keys'
import { addRepositoryFolderToCache } from './shared/utils'

export function useAddFolder() {
  const { server } = useServer()
  const client = useQueryClient()
  const { sendWebsocketMessage } = useChatWebsocketContext()
  return useMutation({
    mutationFn: addFolder,
    onSuccess: async data => {
      const { folder, message } = data
      if (folder.chatGroupId) {
        addRepositoryFolderToCache(client, folder, folder.chatGroupId)
        const recentFilesKey = keys.recent(folder.chatGroupId)
        client.invalidateQueries({ queryKey: recentFilesKey }).catch(() => null)
      }
      if (message) {
        await addMessagesDataToCache(client, [message], undefined)
        sendWebsocketMessage({
          action: 'sendMessageEvent',
          messageEvent: message,
        })
      }
    },
  })

  async function addFolder({
    name,
    description,
    parentFolderId,
    chatGroupId,
  }: AddFolderParams) {
    const response = await server.post<RepositoryResponse.AddFolder>(
      `/v2/chat/repository/folders`,
      getFormData(),
      'user',
      'multipart/form-data'
    )
    const folder = convertFolder(response.folder)
    return { folder, message: response.message }

    function getFormData() {
      const form = new FormData()
      if (name) {
        form.append('name', name)
      }
      if (description) {
        form.append('description', description)
      }
      if (parentFolderId) {
        form.append('parent_folder_id', parentFolderId.toString())
      }
      if (chatGroupId) {
        form.append('chat_group_id', chatGroupId.toString())
      }
      return form
    }
  }
}
